import React from "react";
import { Box, Stack, Tooltip, Typography, alpha, useTheme } from "@mui/material";
import type { FatigueAction } from "../data/types";

interface WellbeingActionMatrixProps {
  actions: FatigueAction[];
  onSelectAction?: (id: string) => void;
}

// Impact (y, higher = more impact) vs Ease (x, higher = easier). Top-right is
// the "quick wins / low-hanging fruit" zone.
const AXIS_MAX = 5;

/** Impact/ease scatter that surfaces low-hanging-fruit wellbeing actions. */
export const WellbeingActionMatrix: React.FC<WellbeingActionMatrixProps> = ({
  actions,
  onSelectAction,
}) => {
  const theme = useTheme();
  const xPct = (ease: number) => ((ease - 0.5) / AXIS_MAX) * 100;
  const yPct = (impact: number) => (1 - (impact - 0.5) / AXIS_MAX) * 100;

  return (
    <Box>
      <Box
        role="img"
        aria-label="Impact versus ease matrix of wellbeing actions. Top-right is high impact and easy to implement."
        sx={{
          position: "relative",
          height: 360,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
          bgcolor: alpha(theme.palette.text.primary, 0.02),
        }}
      >
        {/* Quick-wins quadrant: top-right (high impact, high ease) */}
        <Box
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            width: "50%",
            height: "50%",
            background: `linear-gradient(225deg, ${alpha(theme.palette.success.main, 0.16)}, transparent)`,
          }}
        />
        <Typography
          variant="caption"
          sx={{ position: "absolute", right: 8, top: 6, fontWeight: 700, color: theme.palette.success.main }}
        >
          Quick wins
        </Typography>

        {/* Dividers */}
        <Box sx={{ position: "absolute", left: "50%", top: 0, bottom: 0, borderLeft: `1px dashed ${theme.palette.divider}` }} />
        <Box sx={{ position: "absolute", top: "50%", left: 0, right: 0, borderTop: `1px dashed ${theme.palette.divider}` }} />

        {actions.map((a) => {
          const interactive = Boolean(onSelectAction);
          const isQuickWin = a.impact >= 3 && a.ease >= 3;
          const color = isQuickWin ? theme.palette.success.main : theme.palette.primary.main;
          return (
            <Tooltip key={a.id} arrow title={`${a.title} — impact ${a.impact}/5, ease ${a.ease}/5`}>
              <Box
                role={interactive ? "button" : undefined}
                tabIndex={interactive ? 0 : undefined}
                aria-label={a.title}
                onClick={() => onSelectAction?.(a.id)}
                onKeyDown={(e) => {
                  if (interactive && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    onSelectAction?.(a.id);
                  }
                }}
                sx={{
                  position: "absolute",
                  left: `${xPct(a.ease)}%`,
                  top: `${yPct(a.impact)}%`,
                  transform: "translate(-50%, -50%)",
                  px: 1,
                  py: 0.5,
                  maxWidth: 150,
                  borderRadius: 1.5,
                  bgcolor: alpha(color, 0.16),
                  border: `1px solid ${alpha(color, 0.5)}`,
                  cursor: interactive ? "pointer" : "default",
                  "&:hover": interactive ? { bgcolor: alpha(color, 0.28) } : undefined,
                  "&:focus-visible": { outline: `2px solid ${color}` },
                }}
              >
                <Typography variant="caption" fontWeight={700} noWrap sx={{ display: "block" }}>
                  {a.title}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Harder to implement
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
          Ease →
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Easier
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center" }}>
        Vertical axis: impact on fatigue risk (higher = more impact)
      </Typography>
    </Box>
  );
};
